// 5-point confidence scale for BMD follow-up question B: "How sure are you?".
//
// Values are integers 1..5, unchanged. Labels come from
// BMD_COPY.CONFIDENCE_LABELS so they stay centralized and translatable.
//
// THE WORDS ARE ALWAYS ON SCREEN. They used to appear only under the option the
// student had already picked, which meant the scale had to be decoded before it
// could be used: five faces, no key, and the label arriving after the decision
// it was supposed to inform. A nine-year-old reading "how sure are you?" needs
// to see that the far left means "Guessed" before choosing it.
//
// The smileys are the same five assets - the PM asked to keep them - now in a
// row of cards rather than a bare list.
//
// Modes:
//   interactive  the student picks, and `onChange(value)` fires per click
//   readonly     shows which value was picked, for review and reports, with
//                no interaction

import React from "react"
import { BMD_COPY } from "../../constants/diagnostics/bmd.constants"
import { BMD_UI } from "../../constants/diagnostics/bmd.theme"

// Emoji asset pairs: inactive (em) and active (ema).
import em1 from "../../assets/images/services-icon/em1.svg"
import em2 from "../../assets/images/services-icon/em2.svg"
import em3 from "../../assets/images/services-icon/em3.svg"
import em4 from "../../assets/images/services-icon/em4.svg"
import em5 from "../../assets/images/services-icon/em5.svg"
import ema1 from "../../assets/images/services-icon/ema1.svg"
import ema2 from "../../assets/images/services-icon/ema2.svg"
import ema3 from "../../assets/images/services-icon/ema3.svg"
import ema4 from "../../assets/images/services-icon/ema4.svg"
import ema5 from "../../assets/images/services-icon/ema5.svg"

type ConfidenceOption = {
  value: 1 | 2 | 3 | 4 | 5
  label: string
  inactiveIcon: string
  activeIcon: string
}

const OPTIONS: ConfidenceOption[] = [
  { value: 1, label: BMD_COPY.CONFIDENCE_LABELS[0], inactiveIcon: em1, activeIcon: ema1 },
  { value: 2, label: BMD_COPY.CONFIDENCE_LABELS[1], inactiveIcon: em2, activeIcon: ema2 },
  { value: 3, label: BMD_COPY.CONFIDENCE_LABELS[2], inactiveIcon: em3, activeIcon: ema3 },
  { value: 4, label: BMD_COPY.CONFIDENCE_LABELS[3], inactiveIcon: em4, activeIcon: ema4 },
  { value: 5, label: BMD_COPY.CONFIDENCE_LABELS[4], inactiveIcon: em5, activeIcon: ema5 },
]

export type ConfidenceScaleProps = {
  value: number | null
  onChange?: (value: 1 | 2 | 3 | 4 | 5) => void
  readonly?: boolean
  className?: string
}

export const ConfidenceScale: React.FC<ConfidenceScaleProps> = ({
  value,
  onChange,
  readonly = false,
  className,
}) => {
  const handleClick = (optionValue: 1 | 2 | 3 | 4 | 5) => {
    if (readonly) return
    onChange?.(optionValue)
  }

  return (
    <ul
      className={`list-unstyled d-flex flex-wrap gap-2 mb-0 ${className ?? ""}`}
      role="radiogroup"
      aria-label={BMD_COPY.FOLLOW_UP_CONFIDENCE}
    >
      {OPTIONS.map((option) => {
        const isActive = value === option.value
        return (
          <li
            key={option.value}
            role="radio"
            aria-checked={isActive}
            tabIndex={readonly ? -1 : 0}
            onClick={() => handleClick(option.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                handleClick(option.value)
              }
            }}
            className="d-flex flex-column align-items-center justify-content-start text-center"
            style={{
              flex: "1 1 0",
              minWidth: 74,
              padding: "10px 6px",
              borderRadius: 10,
              border: `${
                isActive
                  ? BMD_UI.CONTROL_SELECTED_BORDER_WIDTH
                  : BMD_UI.CONTROL_BORDER_WIDTH
              }px solid ${
                isActive ? BMD_UI.CONFIDENCE_BORDER : BMD_UI.CONTROL_BORDER
              }`,
              backgroundColor: isActive ? BMD_UI.CONFIDENCE_BG : "#fff",
              cursor: readonly ? "default" : "pointer",
            }}
          >
            <img
              src={isActive ? option.activeIcon : option.inactiveIcon}
              alt=""
              aria-hidden="true"
              style={{ width: 26, height: 26 }}
            />
            <span
              className="mt-2"
              style={{
                fontSize: 11.5,
                lineHeight: 1.25,
                color: isActive ? BMD_UI.CONFIDENCE_TEXT : BMD_UI.BODY,
                fontWeight: isActive ? 700 : 400,
              }}
            >
              {option.label}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

export default ConfidenceScale
